import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Badge } from '../components/ui/badge';
import { 
    Plus, 
    RefreshCw, 
    Bug, 
    Trash2,
    FileText,
    Calendar,
    ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
    new: { label: 'Neu', icon: Plus, color: 'bg-green-500/20 text-green-500 border-green-500/30' },
    update: { label: 'Update', icon: RefreshCw, color: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
    fix: { label: 'Fix', icon: Bug, color: 'bg-orange-500/20 text-orange-500 border-orange-500/30' },
    remove: { label: 'Entfernt', icon: Trash2, color: 'bg-red-500/20 text-red-500 border-red-500/30' }
};

export default function ChangelogPage() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchChangelog();
    }, []);

    const fetchChangelog = async () => {
        try {
            const response = await axios.get(`${API}/changelog?limit=100`);
            setEntries(response.data);
        } catch (error) {
            console.error('Error fetching changelog:', error);
        } finally {
            setLoading(false);
        }
    };

    // Group entries by month
    const groupedEntries = entries.reduce((acc, entry) => {
        const date = new Date(entry.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
        
        if (!acc[monthKey]) {
            acc[monthKey] = { label: monthLabel, entries: [] };
        }
        acc[monthKey].entries.push(entry);
        return acc;
    }, {});

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            <Navbar />

            <div className="pt-24 pb-16 px-6 lg:px-12">
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-12"
                    >
                        <h1 className="text-4xl font-mono font-bold mb-4">Changelog</h1>
                        <p className="text-muted-foreground">
                            Alle Änderungen und Updates der Dokumentation.
                        </p>
                    </motion.div>

                    {loading ? (
                        <div className="text-center py-12">
                            <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto" />
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Noch keine Changelog-Einträge.</p>
                        </div>
                    ) : (
                        <div className="space-y-12">
                            {Object.entries(groupedEntries).map(([monthKey, { label, entries: monthEntries }]) => (
                                <motion.div
                                    key={monthKey}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <h2 className="text-lg font-mono font-semibold mb-4 text-primary">
                                        {label}
                                    </h2>
                                    <div className="space-y-4">
                                        {monthEntries.map((entry, index) => {
                                            const config = typeConfig[entry.type] || typeConfig.update;
                                            const Icon = config.icon;
                                            
                                            return (
                                                <motion.div
                                                    key={entry.id}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.05 }}
                                                    className="relative pl-8 pb-4 border-l-2 border-border last:border-l-0"
                                                >
                                                    {/* Timeline dot */}
                                                    <div className={`absolute -left-2.5 w-5 h-5 rounded-full ${config.color} border flex items-center justify-center`}>
                                                        <Icon className="w-3 h-3" />
                                                    </div>
                                                    
                                                    <div className="bg-card/50 rounded-lg border border-border p-4 ml-4">
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <Badge variant="outline" className={`text-xs ${config.color}`}>
                                                                        {config.label}
                                                                    </Badge>
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {formatDate(entry.created_at)}
                                                                    </span>
                                                                </div>
                                                                <h3 className="font-mono font-medium">{entry.title}</h3>
                                                                <p className="text-sm text-muted-foreground mt-1">
                                                                    {entry.description}
                                                                </p>
                                                            </div>
                                                            
                                                            {/* Link to doc if available */}
                                                            {entry.doc_slug && entry.project_slug && (
                                                                <Link
                                                                    to={`/docs/${entry.project_slug}/${entry.doc_slug}`}
                                                                    className="flex items-center gap-1 text-xs text-primary hover:underline flex-shrink-0"
                                                                >
                                                                    <FileText className="w-3 h-3" />
                                                                    Anzeigen
                                                                    <ArrowRight className="w-3 h-3" />
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
